import { useEffect, useMemo, useState } from "react";
import { motion } from "motion/react";
import {
  LayoutDashboard, Search, Users, RefreshCw, AlertCircle, Loader2,
  Clock, Save, RotateCcw,
} from "lucide-react";
import { getAPI } from "../../../api/frameworksApi";
import { API_URL } from "../../../constants/index";
import { useAuthStore } from "../../../stores/authStore";
import { useToastStore } from "../../../stores/toastStore";
import { ScopePicker, type ScopeGroup, type ScopeOption } from "./ScopePicker";
import { CARD_CATEGORIES, buildCatalog } from "./dashboardConfigData";
import type { DashboardCard } from "./dashboardConfigData";
import { RoleListItem, CardToggle, type WState } from "./DashboardConfigControls";
import {
  fetchWidgetCatalog, fetchRoleWidgets, saveRoleWidgets, resetRoleWidgets,
  fetchMyWidgets, saveMyWidgets,
} from "../GRCDashboardNew/grcWidgets";
import type { WidgetKey, WidgetPin } from "../GRCDashboardNew/grcWidgets";

/* recommendation value that promotes a widget to the "Primary" group. */
const PRIMARY_RECOMMENDATION = "PRIMARY_RECOMMENDATION";

interface Role {
  id: string;
  name?: string;
  displayName: string;
  description?: string;
}

type WidgetMap = Record<string, WidgetKey[]>;                       // roleId → widget enums
/* roleId → per-widget pin state (the editable draft / saved baseline). Widgets
   absent from a role's map fall back to the default state (inherit for admin,
   off for restricted users). */
type DraftMap = Record<string, Partial<Record<WidgetKey, WState>>>;

interface OrgOption { id: string; name: string; type?: string; typeLabel?: string; }

export function DashboardConfig() {
  const authOrgId = useAuthStore((s) => s.user?.orgId) ?? null;
  const toastSuccess = useToastStore((s) => s.showSuccess);
  const toastError = useToastStore((s) => s.showError);
  const toastWarning = useToastStore((s) => s.showWarning);

  const [roles, setRoles] = useState<Role[]>([]);
  const [catalog, setCatalog] = useState<DashboardCard[]>([]);
  const [roleId, setRoleId] = useState<string>("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  /* Organization the config is read/written against. System admins have no org
     on their account, so they pick one here; the selected id is sent with every
     save call. */
  const [orgs, setOrgs] = useState<OrgOption[]>([]);          // flat lookup (groups + orgs)
  const [scopeGroups, setScopeGroups] = useState<ScopeGroup[]>([]); // groups → their orgs
  const [ungroupedOrgs, setUngroupedOrgs] = useState<ScopeOption[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const orgId = selectedOrgId; // effective org/group for all reads/writes
  const selectedScope = orgs.find((o) => o.id === selectedOrgId) ?? null;

  /* Per-role pin state. `draft` holds the admin's pending edits; `baseline` is
     the last saved snapshot — the two are diffed to detect unsaved changes and
     to build the bulk-save body. Edits are buffered locally and only persisted
     when Save is clicked (no per-toggle API calls). */
  const [draft, setDraft] = useState<DraftMap>({});
  const [baseline, setBaseline] = useState<DraftMap>({});
  /* Bulk save in flight → lock the controls + show the spinner. */
  const [saving, setSaving] = useState(false);
  /* Reset-to-inherited confirmation dialog (admin only). */
  const [confirmingReset, setConfirmingReset] = useState(false);
  /* The admin-selected universe per role (role-widgets API). A restricted user
     only sees/toggles cards within this set, so a card they switch off stays
     visible (as "Off") instead of disappearing. */
  const [adminUniverse, setAdminUniverse] = useState<WidgetMap>({});
  /* recommendation tag per widget, per role. Only roles the backend tags (the
     admin role) get entries → drives the Primary/Secondary grouping for them. */
  const [recommendations, setRecommendations] =
    useState<Record<string, Partial<Record<WidgetKey, string>>>>({});
  /* For widgets INHERITED from an ancestor (group/org): the ancestor's name,
     per widget, per role → drives the "Inherited from X" badge. */
  const [inherited, setInherited] =
    useState<Record<string, Partial<Record<WidgetKey, string>>>>({});
  /* Reloading widgets after an org switch → lock controls meanwhile. */
  const [widgetsLoading, setWidgetsLoading] = useState(false);

  /* ── Load roles + catalog + organizations on mount ───────────────────── */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [rolesRes, catalogWidgets, orgsRes] = await Promise.all([
          getAPI(API_URL.PLATFORM_ROLES + "/all"),
          fetchWidgetCatalog(),
          getAPI(`${API_URL.ORG_UNITS}?flat=true`),
        ]);
        if (cancelled) return;
        const rolesList: Role[] = rolesRes?.data ?? [];
        setRoles(rolesList);
        setCatalog(buildCatalog(catalogWidgets));
        setRoleId((prev) => prev || rolesList[0]?.id || "");

        /* Build the scope tree: grants set on a GROUP cascade to every org
           beneath it (an org can still override). The picker shows groups as a
           filter and the orgs under the chosen group beside it. */
        const rawOrgs: Array<{ id: string; name: string; orgType?: string; parentId?: string | null }> =
          orgsRes?.data ?? [];
        const TYPE_LABEL: Record<string, string> = { GROUP: "Group", ORGANIZATION: "Organization" };
        const opt = (o: { id: string; name: string; orgType?: string }): ScopeOption => ({
          id: o.id, name: o.name, type: o.orgType, typeLabel: TYPE_LABEL[o.orgType ?? ""] ?? "",
        });
        type Unit = { id: string; name: string; orgType?: string; parentId?: string | null };
        const byId = new Map<string, Unit>(rawOrgs.map((o) => [o.id, o]));
        /* nearest GROUP ancestor of a unit (walks up parentId) */
        const groupIdOf = (o?: Unit): string | null => {
          let cur: Unit | undefined = o;
          const seen = new Set<string>();
          while (cur && !seen.has(cur.id)) {
            seen.add(cur.id);
            if (cur.orgType === "GROUP") return cur.id;
            cur = cur.parentId ? byId.get(cur.parentId) : undefined;
          }
          return null;
        };
        const groupUnits = rawOrgs.filter((o) => o.orgType === "GROUP");
        const orgUnits = rawOrgs.filter((o) => o.orgType === "ORGANIZATION");
        const orgsByGroup: Record<string, ScopeOption[]> = {};
        const ungrouped: ScopeOption[] = [];
        for (const o of orgUnits) {
          const gid = groupIdOf(o);
          if (gid) (orgsByGroup[gid] ??= []).push(opt(o));
          else ungrouped.push(opt(o));
        }
        const byName = (a: ScopeOption, b: ScopeOption) => a.name.localeCompare(b.name);
        Object.values(orgsByGroup).forEach((list) => list.sort(byName));
        ungrouped.sort(byName);
        const groups: ScopeGroup[] = groupUnits
          .map((g) => ({ id: g.id, name: g.name, orgs: orgsByGroup[g.id] ?? [] }))
          .sort((a, b) => a.name.localeCompare(b.name));

        setScopeGroups(groups);
        setUngroupedOrgs(ungrouped);
        const flat = [...groupUnits.map(opt), ...orgUnits.map(opt)];
        setOrgs(flat);
        /* Default to the user's own org if present, else the first group, else the first org. */
        setSelectedOrgId((prev) =>
          prev ??
          (authOrgId && flat.some((o) => o.id === authOrgId)
            ? authOrgId
            : groups[0]?.id ?? ungrouped[0]?.id ?? null),
        );
      } catch {
        if (!cancelled) toastError("Couldn't load", "Could not load the configuration. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Load EVERY (visible) role's widgets up front for the org ────────────
     Fetched once per org so the role list shows each role's "N cards enabled"
     count immediately. Builds the draft + baseline pin state per role. */
  useEffect(() => {
    if (!orgId || roles.length === 0) {
      setDraft({}); setBaseline({}); setAdminUniverse({}); setRecommendations({}); setInherited({}); return;
    }
    let cancelled = false;
    setWidgetsLoading(true);
    /* Restricted users (e.g. GRC Manager) read their PERSONAL widget state from
       /my-widgets; admins read the role config. */
    const aRoles = useAuthStore.getState().user?.roles ?? [];
    const nrm = (s: string) => s.toLowerCase().replace(/[\s_-]/g, "");
    const isRestrictedUser = !aRoles.some(
      (r) => nrm(r) === "systemadmin" || nrm(r) === "administrator" || nrm(r) === "admin",
    );
    /* Restricted users only manage their own role(s); admins manage all. */
    const rolesToLoad = isRestrictedUser
      ? roles.filter((r) => aRoles.some((ar) => nrm(ar) === nrm(r.displayName ?? r.name ?? "")))
      : roles;
    (async () => {
      try {
        const lists = await Promise.all(
          rolesToLoad.map((r) =>
            fetchRoleWidgets(orgId, r.id)
              .then((w) => [r.id, w] as const)
              .catch(() => [r.id, []] as const),
          ),
        );
        if (cancelled) return;

        const stateMap: DraftMap = {};
        const universeMap: WidgetMap = {};
        const recMap: Record<string, Partial<Record<WidgetKey, string>>> = {};
        const inhMap: Record<string, Partial<Record<WidgetKey, string>>> = {};
        /* widgets inherited from an ancestor → { widget: ancestorName } */
        const inheritedOf = (widgets: readonly { widget: WidgetKey; source?: string; inheritedFrom?: string | null }[]) => {
          const inh: Partial<Record<WidgetKey, string>> = {};
          widgets.forEach((w) => {
            if (w.source === "INHERITED" && w.inheritedFrom) inh[w.widget] = w.inheritedFrom;
          });
          return inh;
        };

        if (isRestrictedUser) {
          /* Universe = the FIXED set the admin chose per role (role-widgets API).
             Personal on/off = my-widgets — so a card the user turned off still
             shows (as Off) instead of disappearing. */
          const my = await fetchMyWidgets();
          if (cancelled) return;
          const block = my.roles.find((r) => r.orgUnitId === orgId) ?? my.roles[0];
          const personalOn = new Set(
            (block?.widgets ?? []).filter((w) => w.enabled).map((w) => w.widget),
          );
          for (const [rid, widgets] of lists) {
            const universeIds = widgets.map((w) => w.widget);
            universeMap[rid] = universeIds;
            const st: Partial<Record<WidgetKey, WState>> = {};
            universeIds.forEach((id) => { st[id] = personalOn.has(id) ? "on" : "off"; });
            stateMap[rid] = st;
            recMap[rid] = {};
            inhMap[rid] = inheritedOf(widgets);
          }
        } else {
          /* Admin role config. The GET returns only effective/granted widgets, so
             every widget in the list is On; anything absent is Off. */
          for (const [rid, widgets] of lists) {
            const ids = widgets.map((w) => w.widget);
            const rec: Partial<Record<WidgetKey, string>> = {};
            const st: Partial<Record<WidgetKey, WState>> = {};
            widgets.forEach((w) => {
              st[w.widget] = "on";
              if (w.recommendation) rec[w.widget] = w.recommendation;
            });
            stateMap[rid] = st;
            universeMap[rid] = ids;
            recMap[rid] = rec;
            inhMap[rid] = inheritedOf(widgets);
          }
        }

        /* baseline is a separate object graph so editing the draft never mutates it. */
        const baseMap: DraftMap = {};
        Object.keys(stateMap).forEach((k) => { baseMap[k] = { ...stateMap[k] }; });

        setDraft(stateMap);
        setBaseline(baseMap);
        setAdminUniverse(universeMap);
        setRecommendations(recMap);
        setInherited(inhMap);
      } catch {
        if (!cancelled) toastError("Couldn't load", "Could not load the configuration. Please try again.");
      } finally {
        if (!cancelled) setWidgetsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, roles]);

  const activeRole = roles.find((r) => r.id === roleId);
  /* The fixed set of cards a restricted user may choose from (admin's selection). */
  const universe = useMemo(() => new Set(adminUniverse[roleId] ?? []), [adminUniverse, roleId]);

  /* The Primary/Secondary recommendation grouping is for the ORG ADMIN login.
     A logged-in SYSTEM_ADMIN always sees the full catalog grouped by category
     (everything), regardless of which role is selected. */
  const authRoles = useAuthStore((s) => s.user?.roles) ?? [];
  const norm = (s: string) => s.toLowerCase().replace(/[\s_-]/g, "");
  const isSystemAdmin = authRoles.some((r) => norm(r) === "systemadmin");
  const isOrgAdmin = authRoles.some((r) => norm(r) === "administrator" || norm(r) === "admin");
  /* Admins manage every role; any other user (e.g. GRC Manager) only sees and
     manages their own role(s). */
  const canSeeAllRoles = isSystemAdmin || isOrgAdmin;
  /* A non-admin (e.g. GRC Manager) is restricted to the cards the admin enabled
     for their role, but can still pick which of those to show on their landing page. */
  const restricted = !canSeeAllRoles;
  const visibleRoles = useMemo(
    () =>
      canSeeAllRoles
        ? roles
        : roles.filter((r) => authRoles.some((ar) => norm(ar) === norm(r.displayName ?? r.name ?? ""))),
    [canSeeAllRoles, roles, authRoles], // eslint-disable-line react-hooks/exhaustive-deps
  );

  /* Keep the selected role within the visible set. */
  useEffect(() => {
    if (visibleRoles.length && !visibleRoles.some((r) => r.id === roleId)) {
      setRoleId(visibleRoles[0].id);
    }
  }, [visibleRoles, roleId]);

  /* Cards are a simple On/Off switch for everyone — a widget with no pin is Off. */
  const defaultState: WState = "off";
  const stateOf = (id: WidgetKey): WState => draft[roleId]?.[id] ?? defaultState;

  /* Effective "enabled" count for a role: explicit On plus widgets still set to
     Inherit that an ancestor turns on. Drives the role-list "N cards" labels. */
  const effectiveOnCount = (rid: string) => {
    const d = draft[rid] ?? {};
    const inh = inherited[rid] ?? {};
    const keys = new Set<string>([...Object.keys(d), ...Object.keys(inh)]);
    let n = 0;
    keys.forEach((k) => {
      const st = d[k as WidgetKey] ?? defaultState;
      if (st === "on") n++;
      else if (st === "inherit" && inh[k as WidgetKey]) n++;
    });
    return n;
  };
  const currentOnCount = effectiveOnCount(roleId);

  /* Unsaved changes on the current role → enables the Save button. Compared
     against the saved baseline, treating absent widgets as the default state. */
  const dirty = useMemo(() => {
    const d = draft[roleId] ?? {};
    const b = baseline[roleId] ?? {};
    const keys = new Set<string>([...Object.keys(d), ...Object.keys(b)]);
    for (const k of keys) {
      if ((d[k as WidgetKey] ?? defaultState) !== (b[k as WidgetKey] ?? defaultState)) return true;
    }
    return false;
  }, [draft, baseline, roleId, defaultState]);

  /* ── Buffered edits (no API call until Save) ──────────────────────────── */
  const setWidgetState = (id: WidgetKey, next: WState) =>
    setDraft((prev) => ({ ...prev, [roleId]: { ...(prev[roleId] ?? {}), [id]: next } }));

  const setMany = (ids: WidgetKey[], next: WState) =>
    setDraft((prev) => {
      const cur = { ...(prev[roleId] ?? {}) };
      ids.forEach((id) => { cur[id] = next; });
      return { ...prev, [roleId]: cur };
    });

  /* ── Save the current role+org in one transaction ─────────────────────── */
  const handleSave = async () => {
    if (!orgId || !roleId || saving || !dirty) return;
    setSaving(true);
    try {
      if (restricted) {
        /* Personal preference: batch only the changed widgets (no bulk endpoint). */
        const d = draft[roleId] ?? {};
        const b = baseline[roleId] ?? {};
        const keys = new Set<string>([...Object.keys(d), ...Object.keys(b)]);
        const changes: WidgetPin[] = [];
        keys.forEach((k) => {
          const cur = d[k as WidgetKey] ?? defaultState;
          const prevS = b[k as WidgetKey] ?? defaultState;
          if (cur !== prevS) changes.push({ widget: k as WidgetKey, enabled: cur === "on" });
        });
        const my = await saveMyWidgets(orgId, changes);
        const block = my.roles.find((r) => r.orgUnitId === orgId) ?? my.roles[0];
        const personalOn = new Set(
          (block?.widgets ?? []).filter((w) => w.enabled).map((w) => w.widget),
        );
        const st: Partial<Record<WidgetKey, WState>> = {};
        (adminUniverse[roleId] ?? []).forEach((id) => { st[id] = personalOn.has(id) ? "on" : "off"; });
        setDraft((prev) => ({ ...prev, [roleId]: st }));
        setBaseline((prev) => ({ ...prev, [roleId]: { ...st } }));
      } else {
        /* Bulk role config: every catalog widget is sent explicitly as
           On (enabled:true) / Off (enabled:false) so the level is deterministic. */
        const pins: WidgetPin[] = catalog.map((c) => ({
          widget: c.id, enabled: stateOf(c.id) === "on",
        }));
        const widgets = await saveRoleWidgets(orgId, roleId, pins);
        /* Response lists the effective (granted) widgets → those are On, rest Off. */
        const effectiveOn = new Set(widgets.map((w) => w.widget));
        const st: Partial<Record<WidgetKey, WState>> = {};
        catalog.forEach((c) => { st[c.id] = effectiveOn.has(c.id) ? "on" : "off"; });
        setDraft((prev) => ({ ...prev, [roleId]: st }));
        setBaseline((prev) => ({ ...prev, [roleId]: { ...st } }));
        /* NOTE: recommendations are NOT updated here. They are read-only metadata
           set by the SYSTEM_ADMIN. The PUT response for on/off changes does not
           return recommendation tags, so touching them would wipe the Primary/
           Secondary grouping. */
      }
      toastSuccess(
        "Configuration saved",
        `${activeRole?.displayName ?? "Role"} dashboard cards updated successfully.`,
      );
    } catch {
      toastError("Save failed", "Could not save the configuration. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  /* ── Reset the level → follow the parent (admin only) ─────────────────────
     Clears ALL of the role's explicit pins at this org so it inherits again.
     The DELETE returns the now-inherited view (or empty) → re-render from it. */
  const handleReset = async () => {
    if (!orgId || !roleId || saving || restricted) return;
    setConfirmingReset(false);
    setSaving(true);
    try {
      const widgets = await resetRoleWidgets(orgId, roleId);
      /* After a reset the level follows the parent — the returned (inherited)
         widgets are shown On, everything else Off. */
      const effectiveOn = new Set(widgets.map((w) => w.widget));
      const st: Partial<Record<WidgetKey, WState>> = {};
      catalog.forEach((c) => { st[c.id] = effectiveOn.has(c.id) ? "on" : "off"; });
      setDraft((prev) => ({ ...prev, [roleId]: st }));
      setBaseline((prev) => ({ ...prev, [roleId]: { ...st } }));
      /* recommendations are NOT rebuilt — they are read-only metadata from the
         initial load and the reset response does not include them. */
      toastSuccess(
        "Reset to inherited",
        `${activeRole?.displayName ?? "Role"} now follows the parent's dashboard cards.`,
      );
    } catch {
      toastError("Reset failed", "Could not reset this level. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  /* The SYSTEM_ADMIN's recommendation tags for the selected role. */
  const roleRec = useMemo(() => recommendations[roleId] ?? {}, [recommendations, roleId]);
  /* Administrator login with no recommendations chosen yet for this role → show a
     "waiting for the system administrator" message instead of dumping every card
     into Secondary. */
  const awaitingRecommendation = isOrgAdmin && Object.keys(roleRec).length === 0;

  /* ── Catalog sections ──────────────────────────────────────────────────
     Grouping depends on WHO is logged in (not which role is selected):
       • Administrator → Primary / Secondary recommendation (only once the
         SYSTEM_ADMIN has chosen; otherwise the waiting message is shown).
       • SYSTEM_ADMIN / restricted users → grouped by category. */
  const sections = useMemo<{ title: string; cards: DashboardCard[] }[]>(() => {
    /* Restricted users (e.g. GRC Manager) only see the cards the admin selected
       for their role; admins see the full catalog. */
    const base = restricted ? catalog.filter((c) => universe.has(c.id)) : catalog;
    if (awaitingRecommendation) return [];
    if (isOrgAdmin) {
      return [
        { title: "Primary Recommendation",   cards: base.filter((c) => roleRec[c.id] === PRIMARY_RECOMMENDATION) },
        { title: "Secondary Recommendation", cards: base.filter((c) => roleRec[c.id] !== PRIMARY_RECOMMENDATION) },
      ];
    }
    return CARD_CATEGORIES.map((title) => ({
      title,
      cards: base.filter((c) => c.category === title),
    }));
  }, [roleRec, catalog, isOrgAdmin, awaitingRecommendation, restricted, universe]);

  /* ── search filter ─────────────────────────────────────────────────────── */
  const q = search.trim().toLowerCase();
  const matches = (c: DashboardCard) =>
    !q || c.title.toLowerCase().includes(q) || c.description.toLowerCase().includes(q);

  return (
    <div className="min-h-full">
      {/* Header */}
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-[#A78BFA]/10">
            <LayoutDashboard className="size-5 text-[#A78BFA]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Insights Configuration</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Choose which cards each role sees on their landing page after login
            </p>
          </div>
        </div>

        {/* Scope selector — groups (cascade) on the left, their orgs on the right */}
        <ScopePicker
          groups={scopeGroups}
          ungrouped={ungroupedOrgs}
          selectedId={selectedOrgId}
          selected={selectedScope}
          onSelect={(id) => {
            if (id === selectedOrgId) return;
            if (dirty) toastWarning("Unsaved changes discarded", "Your unsaved changes were discarded.");
            setSelectedOrgId(id);
            /* New scope → drop cached state so roles refetch against it. */
            setDraft({});
            setBaseline({});
            setAdminUniverse({});
            setRecommendations({});
            setInherited({});
          }}
        />
      </div>

      {/* No-org guard */}
      {!loading && !orgId && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/[0.08] px-3 py-2.5 text-sm text-amber-500">
          <AlertCircle className="size-4 shrink-0" />
          {orgs.length === 0
            ? "No organizations available for your account."
            : "Select an organization to configure its dashboards."}
        </div>
      )}

      {loading ? (
        <div className="flex min-h-[50vh] items-center justify-center">
          <RefreshCw className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px_1fr]">
          {/* -- Role list ---------------------------------------------------- */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 px-1">
              <Users className="size-4 text-muted-foreground" />
              <span className="text-[11px] font-semibold tracking-wider text-muted-foreground">
                Roles ({visibleRoles.length})
              </span>
            </div>
            <div className="flex flex-col gap-2">
              {visibleRoles.map((role) => (
                <RoleListItem
                  key={role.id}
                  role={role}
                  active={role.id === roleId}
                  count={effectiveOnCount(role.id)}
                  onClick={() => setRoleId(role.id)}
                />
              ))}
            </div>
          </div>

          {/* -- Card catalog for the selected role -------------------------- */}
          <div className="rounded-xl border border-border bg-card">
            {/* Catalog header */}
            <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="truncate text-base font-bold text-foreground">
                    {activeRole?.displayName ?? "Select a role"}
                  </h2>
                  {widgetsLoading && (
                    <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />
                  )}
                </div>
                <p className="mt-0.5 text-sm font-medium text-foreground/80">
                  {restricted
                    ? `${currentOnCount} of ${universe.size} cards shown on your landing page`
                    : `${currentOnCount} of ${catalog.length} cards enabled on this landing page`}
                </p>
              </div>
              {/* Reset (admin) + Save button + unsaved-changes hint */}
              <div className="flex flex-wrap items-center gap-2.5 self-start sm:self-auto">
                {dirty && !saving && (
                  <span className="text-[11px] font-semibold text-amber-500">Unsaved changes</span>
                )}
                {!restricted && (
                  <button
                    type="button"
                    onClick={() => setConfirmingReset(true)}
                    disabled={saving || widgetsLoading}
                    title="Remove all custom widget settings for this role at this org and follow the parent"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:border-rose-400/40 hover:text-rose-500 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <RotateCcw className="size-3.5" />
                    Reset to inherited
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!dirty || saving || widgetsLoading}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-500/40 bg-indigo-600 px-3.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-indigo-500 disabled:cursor-not-allowed disabled:border-border disabled:bg-foreground/10 disabled:text-muted-foreground"
                >
                  {saving ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
                  {saving ? "Saving" : "Save"}
                </button>
              </div>
            </div>

            {/* Search */}
            <div className="border-b border-border p-4">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search cards by name to enable or disable"
                  maxLength={80}
                  className="h-10 w-full rounded-lg border border-border bg-background pl-9 pr-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-indigo-500/50"
                />
              </div>
            </div>

            {/* Grouped catalog */}
            <div className="flex flex-col gap-6 p-4">
              {sections.map((section) => {
                const cards = section.cards.filter(matches);
                if (cards.length === 0) return null;

                const sectionIds = section.cards.map((c) => c.id);
                const onInSection = sectionIds.filter((id) => stateOf(id) === "on").length;
                const allOn = sectionIds.length > 0 && onInSection === sectionIds.length;
                /* Bulk action: turn the whole section On, or Off when all are On. */
                const bulkNext: WState = allOn ? "off" : "on";
                const bulkLabel = allOn ? "Disable all" : "Enable all";

                return (
                  <section key={section.title}>
                    <div className="mb-2.5 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-bold text-foreground">
                          {section.title}
                        </h3>
                        <span className="text-[11px] tabular-nums text-muted-foreground">
                          {`${onInSection}/${sectionIds.length}`}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setMany(sectionIds, bulkNext)}
                        disabled={widgetsLoading || saving}
                        className="text-[11px] font-semibold text-indigo-500 transition-colors hover:text-indigo-400 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {bulkLabel}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {cards.map((card) => (
                        <CardToggle
                          key={card.id}
                          card={card}
                          mode="binary"
                          state={stateOf(card.id as WidgetKey)}
                          saving={false}
                          disabled={widgetsLoading || saving}
                          inheritedFrom={inherited[roleId]?.[card.id as WidgetKey]}
                          onChange={(next) => setWidgetState(card.id as WidgetKey, next)}
                        />
                      ))}
                    </div>
                  </section>
                );
              })}

              {awaitingRecommendation ? (
                <div className="flex flex-col items-center justify-center gap-3 px-4 py-14 text-center">
                  <div className="flex size-12 items-center justify-center rounded-full bg-amber-500/10">
                    <Clock className="size-6 text-amber-500" />
                  </div>
                  <div className="max-w-sm">
                    <p className="text-sm font-semibold text-foreground">
                      Waiting for the system administrator
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                      No recommended cards have been chosen for{" "}
                      {activeRole?.displayName ?? "this role"} yet. Once the system administrator
                      marks their recommendations, they'll appear here as Primary and Secondary.
                    </p>
                  </div>
                </div>
              ) : (
                !sections.some((s) => s.cards.some(matches)) && (
                  <div className="flex h-32 items-center justify-center px-4 text-center">
                    <span className="text-sm text-muted-foreground">
                      {q
                        ? `No cards match "${search}"`
                        : restricted && universe.size === 0
                          ? "Your administrator hasn't selected any cards for your role yet."
                          : "No widgets available"}
                    </span>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reset-to-inherited confirmation */}
      {confirmingReset && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setConfirmingReset(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-xl border border-border bg-card p-5 shadow-xl"
          >
            <div className="flex items-start gap-3">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-rose-500/10">
                <RotateCcw className="size-5 text-rose-500" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-bold text-foreground">Reset to inherited?</h3>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  This will remove all custom widget settings for{" "}
                  <span className="font-semibold text-foreground">{activeRole?.displayName ?? "this role"}</span>{" "}
                  at <span className="font-semibold text-foreground">{selectedScope?.name ?? "this organization"}</span>{" "}
                  and follow the parent.
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmingReset(false)}
                className="rounded-lg border border-border bg-background px-3.5 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-foreground/[0.04]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReset}
                className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-rose-500"
              >
                <RotateCcw className="size-4" />
                Reset to inherited
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
